import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAppId } from '../context/AppIdContext';
import '../style/speakSearch.css';
import Header from '../component/header'; // Added Header import

const BASE_URL = process.env.REACT_APP_BASE_URL;

const BoothWisePhonebook = () => {
    // --- State ---
    const [boothData, setBoothData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    // --- Hooks ---
    const navigate = useNavigate();

    // --- Context ---
    const { appId, language, userId } = useAppId();
    const lang = language || 'en';

    // --- Translation Dictionary ---
    const t = {
        en: {
            pageTitle: "Booth Wise List",
            filterPlaceholder: "Search by Booth Number...",
            loadingData: "Loading Data...",
            noData: "No records found.",
            boothNo: "Booth No.",
            relativeVoter: "Relative Voter"
        },
        hi: {
            pageTitle: "बूथ वार सूची",
            filterPlaceholder: "बूथ नंबर द्वारा खोजें...",
            loadingData: "डेटा लोड हो रहा है...",
            noData: "कोई रिकॉर्ड नहीं मिला।",
            boothNo: "बूथ नं.",
            relativeVoter: "सापेक्ष मतदाता"
        },
        gu: {
            pageTitle: "બૂથ વાઈઝ યાદી",
            filterPlaceholder: "બૂથ નંબરથી શોધો...",
            loadingData: "ડેટા લોડ થઈ રહ્યો છે...",
            noData: "કોઈ રેકોર્ડ મળ્યા નથી.",
            boothNo: "બૂથ નં.",
            relativeVoter: "સંબંધિત મતદાર"
        }
    };

    const txt = t[lang];

    // --- API Fetch Logic ---
    const fetchBoothData = async () => {
        if (!appId || !userId) return;

        setLoading(true);
        setSearchTerm("");

        try {
            const response = await axios.post(
                `${BASE_URL}/api/dis_booth_wise_phonebook`,
                {
                    app_id: appId,
                    user_id: userId
                }
            );

            if (response.data.success && Array.isArray(response.data.data.result)) {
                setBoothData(response.data.data.result);
            } else {
                setBoothData([]);
            }
        } catch (error) {
            console.error("Error fetching booth data:", error);
            setBoothData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBoothData();
    }, [appId, userId]);

    // --- Filter Logic ---
    const filteredData = boothData.filter((item) => {
        if (!searchTerm) return true;
        return String(item.booth_no).includes(searchTerm);
    });

    // --- Navigation Handler ---
    const handleCardClick = (boothNo) => {
        navigate('/dis_booth_wise_phonebook_member', { state: { booth_no: boothNo } });
    };

    return (
        <div className='app-container container-fluid p-4'>

            {/* Header Component Integrated Here */}
            <Header
                title={txt.pageTitle}
                subtitle={
                    !loading ? (
                        <span>
                            <span className="count-badge">{filteredData.length}</span> {lang === 'en' ? 'booths found' : 'बूथ मिले'}
                        </span>
                    ) : " "
                }
            />

            {/* Modern Search Filter Row */}
            {boothData.length > 0 && (
                <div className="row mb-5 mt-3">
                    <div className="col-12">
                        <div className="d-flex justify-content-end align-items-center flex-wrap gap-3">
                            <div className="search-wrapper-modern-container">
                                <div className="search-wrapper-modern w-100">
                                    <svg className="search-icon-modern" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                    <input
                                        className="search-input-modern"
                                        type="number"
                                        placeholder={txt.filterPlaceholder}
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Loading Spinner */}
            {loading && (
                <div className="row">
                    <div className="col-12">
                        <div className="loader-wrapper">
                            <div className="spinner-modern"></div>
                            <p className="loader-text">{txt.loadingData}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* No Data State */}
            {!loading && filteredData.length === 0 && (
                <div className="row">
                    <div className="col-12 text-center text-muted p-5 empty-state">
                        <div className="empty-icon">📊</div>
                        <p>{txt.noData}</p>
                    </div>
                </div>
            )}

            {/* --- DATA GRID --- */}
            <div className="row g-3 pb-5">
                {!loading && filteredData.map((item, index) => (
                    <div className="col-6 col-sm-4 col-md-3 col-lg-2" key={index}>
                        <div
                            className="clean-stat-card"
                            onClick={() => handleCardClick(item.booth_no)}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="card-title">
                                {txt.boothNo} {item.booth_no}
                            </div>
                            <div className="card-subtitle">
                                {txt.relativeVoter}
                            </div>
                            <div className="card-value">
                                {item.total}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <style jsx>{`
                /* --- SEARCH BAR STYLE --- */
                .search-wrapper-modern-container {
                    width: 100%;
                    max-width: 350px;
                }
                .search-wrapper-modern { 
                    position: relative; 
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
                }
                .search-icon-modern { 
                    position: absolute; 
                    left: 14px; 
                    top: 50%; 
                    transform: translateY(-50%); 
                    width: 20px; 
                    height: 20px; 
                    color: #94a3b8; 
                    pointer-events: none; 
                }
                .search-input-modern { 
                    width: 100%; 
                    padding: 12px 16px 12px 42px; 
                    border: none; 
                    border-radius: 12px; 
                    font-size: 14px; 
                    color: #1e293b; 
                    background-color: transparent; 
                    outline: none; 
                    transition: all 0.2s ease; 
                }
                .search-wrapper-modern:focus-within { 
                    border-color: #2563eb; 
                    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1); 
                }

                /* --- CARD DESIGN --- */
                .clean-stat-card {
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 16px 8px;
                    text-align: center;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    transition: transform 0.2s, box-shadow 0.2s, border-color 0.2s;
                    height: 100%;
                    min-height: 110px;
                }
                .clean-stat-card:hover {
                    border-color: #3b82f6;
                    box-shadow: 0 4px 12px -1px rgba(59, 130, 246, 0.15);
                    transform: translateY(-2px);
                }
                .card-title {
                    font-size: 0.95rem;
                    font-weight: 700;
                    color: #1e293b;
                    margin-bottom: 4px;
                }
                .card-subtitle {
                    font-size: 0.75rem;
                    font-weight: 600;
                    color: #475569;
                    margin-bottom: 8px;
                    letter-spacing: 0.3px;
                }
                .card-value {
                    font-size: 1.5rem;
                    font-weight: 800;
                    color: #0ea5e9;
                    line-height: 1;
                }
                .count-badge {
                    background: #e2e8f0;
                    color: #475569;
                    padding: 2px 8px;
                    border-radius: 12px;
                    font-weight: 700;
                    font-size: 0.85rem;
                }
                .spinner-modern {
                    width: 40px; height: 40px; 
                    border: 3px solid #e2e8f0; 
                    border-top: 3px solid #3b82f6; 
                    border-radius: 50%; 
                    animation: spin 0.8s ease-in-out infinite;
                }
                .loader-wrapper { display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 50vh; width: 100%; }
                .loader-text { color: #6B7280; font-weight: 500; margin-top: 15px; }
                .empty-state { background: #f8fafc; border-radius: 20px; border: 2px dashed #e2e8f0; }
                .empty-icon { font-size: 3rem; margin-bottom: 10px; opacity: 0.5; }
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
            `}</style>
        </div>
    );
};

export default BoothWisePhonebook;